import { get, compact, size, isArray, isNil } from 'lodash'

const DEMOGRAPHICS_ONLY_ERROR_KEYS = ['demographics_race', 'demographics_gender', 'demographics_age_groups']

const ISO_DATE_ONLY_LENGTH = 10

function toISODateOnlyString(value) {
  if (!value) return ''
  if (typeof value === 'string') return value.slice(0, ISO_DATE_ONLY_LENGTH)
  if (value instanceof Date) return value.toISOString().slice(0, ISO_DATE_ONLY_LENGTH)
  return ''
}

function toFormArray(value, copyIfArray = false) {
  if (isArray(value)) return copyIfArray ? [...value] : value
  if (!isNil(value) && value !== '') return [value]
  return []
}

function normalizeMultiSelectToRefIds(items) {
  if (!Array.isArray(items)) return []
  return items.map((item) =>
    typeof item === 'object' && item !== null ? item.id || item.ref : item
  )
}

// Enforce single selection for core_businesses to align with the planned single-value input field in the future
function coreBusinessesVettingApiToFormInitialValue(apiCoreBusinesses) {
  const arr = normalizeMultiSelectToRefIds(apiCoreBusinesses)
  if (arr.length > 1) return ''
  return arr.length ? arr[0] : ''
}

function coreBusinessesFormValueToArray(raw) {
  let list
  if (Array.isArray(raw)) { list = raw} 
  else if (raw != null && raw !== '') { list = [raw]}
  else { list = [] }
  return normalizeMultiSelectToRefIds(list)
}

function normalizeDaysOfWeekForVettingPayload(raw) {
  if (!Array.isArray(raw)) return []
  return raw
    .map((v) => (v === '' || v === null || v === undefined ? 0 : Number(v)))
    .filter((day) => day >= 1 && day <= 7)
}

export function vettingFormHasValidationErrors(errors) {
  if (!errors || typeof errors !== 'object') return false
  return Object.values(errors).some((v) =>
    typeof v === 'string' ? true : vettingFormHasValidationErrors(v)
  )
}

function demographicsRowsToFormArrays(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return { demographics_race: [], demographics_gender: [], demographics_age_groups: [] }
  }
  const races = [...new Set(rows.map((r) => r.race).filter(Boolean))]
  const sexes = [...new Set(rows.map((r) => r.sex).filter(Boolean))]
  const ageGroups = [...new Set(rows.map((r) => r.age_group).filter(Boolean))]
  return {
    demographics_race: races,
    demographics_gender: sexes,
    demographics_age_groups: ageGroups
  }
}

function applyDemographicsFromRowsWhenPresent(initialDemographics, rows) {
  if (!Array.isArray(rows) || size(rows) === 0) return initialDemographics
  const fromRows = demographicsRowsToFormArrays(rows)
  return DEMOGRAPHICS_ONLY_ERROR_KEYS.reduce((acc, key) => {
    acc[key] = size(fromRows[key]) > 0 ? fromRows[key] : initialDemographics[key]
    return acc
  }, {})
}

export function vettingToFormInitialValues(vetting, demographicsFromList = null) {
  if (!vetting) return getDefaultInitialValues()

  const toBool = (v) => (v === true || v === 'true' || v === 1)

  const vettingDemographicsRows = Array.isArray(get(vetting, 'vetting_demographics'))
    ? get(vetting, 'vetting_demographics')
    : (Array.isArray(get(vetting, 'demographics')) ? get(vetting, 'demographics') : [])

  let demographics = {
    demographics_race: compact([get(vetting, 'demographics.race')]),
    demographics_gender: compact([get(vetting, 'demographics.sex')]),
    demographics_age_groups: compact([get(vetting, 'demographics.age_group')])
  }

  demographics = (Array.isArray(demographicsFromList) && size(demographicsFromList) > 0)
    ? applyDemographicsFromRowsWhenPresent(demographics, demographicsFromList)
    : applyDemographicsFromRowsWhenPresent(demographics, vettingDemographicsRows)
  const { demographics_race, demographics_gender, demographics_age_groups } = demographics

  const feedingHistory = get(vetting, 'feeding_history') ?? vetting.feeding_history
  const feedingHistoryDate = toISODateOnlyString(feedingHistory)

  return {
    id: vetting.id,
    status: vetting.status || 'vetting_in_progress',
    company: {
      id: get(vetting, 'company'),
      name: get(vetting, 'company_legal_name', ''),
      website: get(vetting, 'company_website', ''),
      slug: '',
      core_businesses: coreBusinessesVettingApiToFormInitialValue(vetting.core_businesses),
      target_population: normalizeMultiSelectToRefIds(vetting.population_groups),
      programmes_and_services: get(vetting, 'program_and_services', ''),
      distribution_channel: normalizeMultiSelectToRefIds(get(vetting, 'distribution_channels')),
      has_sla: toBool(get(vetting, 'has_sla')),
      has_consent_form: toBool(get(vetting, 'has_consent_form')),
      has_policy_documents: toBool(get(vetting, 'has_policy_doc')),
      additionalComments: get(vetting, 'additional_comments', null),
      full_time_females: get(vetting, 'full_time_females', null),
      full_time_males: get(vetting, 'full_time_males', null),
      number_of_volunteers: get(vetting, 'volunteer_number', null),
      reliance_on_sa_harvest: get(vetting, 'level_of_reliance', ''),
      transport_capacity: get(vetting, 'transport_capacity', ''),
      proposal_writing_capabilities: get(vetting, 'proposal_writing_capabilities', ''),
      digital_capabilities: get(vetting, 'digital_capabilities', ''),
      meal_distribution: get(vetting, 'meal_distribution', ''),
      meals_provided: normalizeMultiSelectToRefIds(get(vetting, 'meals_provided')),
      days_of_week: (() => { const d = get(vetting, 'days_of_week'); return isArray(d) ? [...d] : [] })(),
      feeding_history_last_date: feedingHistoryDate,
      feeding_frequency: get(vetting, 'feeding_frequency', ''),
      number_of_people_served: get(vetting, 'number_of_people_served', ''),
      is_registered_npo: toBool(get(vetting, 'is_registered_npo')),
      npo_number: get(vetting, 'npo_number', ''),
      npo_certificate_upload: get(vetting, 'npo_certificate_file') ?? null,
      is_registered_with_dsd: toBool(get(vetting, 'is_registered_dsd')),
      dsd_registration_number: get(vetting, 'dsd_registration_number', ''),
      pbo_certificate_upload: get(vetting, 'dsd_certificate_file') ?? null,
      food_storage_adequate: normalizeMultiSelectToRefIds(get(vetting, 'food_storage_choices')),
      kitchen_facility_images: get(vetting, 'images') ?? null,
      has_kitchen_cleanliness: toBool(get(vetting, 'has_kitchen_cleanliness')),
      has_access_to_water: toBool(get(vetting, 'has_access_to_water')),
      has_hygiene_facilities: toBool(get(vetting, 'has_hygiene_facilities')),
      is_pest_free: toBool(get(vetting, 'is_pest_free')),
      infrastructure_checks_adequate: normalizeMultiSelectToRefIds(get(vetting, 'infrastructure_areas')),
      has_access_for_delivery: toBool(get(vetting, 'has_access_for_delivery')),
      has_parking_security: toBool(get(vetting, 'has_parking_security')),
      has_proximity_to_police: toBool(get(vetting, 'has_proximity_to_police')),
      demographics_race,
      demographics_gender,
      demographics_age_groups,
      number_females_served: get(vetting, 'number_females_served', ''),
      number_males_served: get(vetting, 'number_males_served', ''),
      number_african_served: get(vetting, 'number_african_served', ''),
      number_coloured_served: get(vetting, 'number_coloured_served', ''),
      number_indian_asian_served: get(vetting, 'number_indian_asian_served', ''),
      number_white_served: get(vetting, 'number_white_served', ''),
      addresses: get(vetting, 'addresses', []) || [],
      contacts: get(vetting, 'contacts', []) || [],
    }
  }
}

export function formValuesToVettingPayload(values) {
  const c = values.company || {}

  //preserve 0 number inputs
  const toIntOrNull = (v) => {
    if (v === '' || v === null || v === undefined) return null
    const n = Number(v)
    return (typeof n === 'number' && !Number.isNaN(n) && n >= 0) ? Math.floor(n) : null
  }
  const days = normalizeDaysOfWeekForVettingPayload(c.days_of_week)
  const dateStr = c.feeding_history_last_date
  const feedingHistory =
    dateStr && dateStr.length >= ISO_DATE_ONLY_LENGTH ? `${dateStr}T00:00:00.000Z` : new Date().toISOString()

  const isNewVetting = !values.id
  const payload = {
    company: isNewVetting ? null : (c.id || null),
    company_legal_name: c.name || '',
    company_website: c.website || '',
    program_and_services: c.programmes_and_services || '',
    status: values.status || 'vetting_in_progress',
    feeding_frequency: c.feeding_frequency || '',
    number_of_people_served: (toIntOrNull(c.number_of_people_served)),
    level_of_reliance: c.reliance_on_sa_harvest || '',
    transport_capacity: c.transport_capacity || '',
    meals_provided: normalizeMultiSelectToRefIds(c.meals_provided),
    additional_comments: c.additionalComments || null,
    proposal_writing_capabilities: c.proposal_writing_capabilities || '',
    digital_capabilities: c.digital_capabilities || '',
    full_time_females: toIntOrNull(c.full_time_females),
    full_time_males: toIntOrNull(c.full_time_males),
    volunteer_number: toIntOrNull(c.number_of_volunteers),
    days_of_week: days,
    feeding_history: feedingHistory,
    has_kitchen_cleanliness: !!c.has_kitchen_cleanliness,
    has_access_to_water: !!c.has_access_to_water,
    has_hygiene_facilities: !!c.has_hygiene_facilities,
    is_pest_free: !!c.is_pest_free,
    has_access_for_delivery: !!c.has_access_for_delivery,
    has_parking_security: !!c.has_parking_security,
    has_proximity_to_police: !!c.has_proximity_to_police,
    has_sla: !!c.has_sla,
    has_consent_form: !!c.has_consent_form,
    has_policy_doc: !!c.has_policy_documents,
    is_registered_npo: !!c.is_registered_npo,
    npo_number: c.npo_number || null,
    npo_certificate_file: c.npo_certificate_upload || null,
    is_registered_dsd: !!c.is_registered_with_dsd,
    dsd_registration_number: c.dsd_registration_number || null,
    dsd_certificate_file: c.pbo_certificate_upload || null,
    core_businesses: coreBusinessesFormValueToArray(c.core_businesses),
    population_groups: normalizeMultiSelectToRefIds(c.target_population),
    distribution_channels: normalizeMultiSelectToRefIds(c.distribution_channel),
    food_storage_choices: normalizeMultiSelectToRefIds(c.food_storage_adequate),
    infrastructure_areas: normalizeMultiSelectToRefIds(c.infrastructure_checks_adequate),
    images: c.kitchen_facility_images
  }

  if (values.id) payload.id = values.id
  return payload
}

export function formValuesToVettingDemographicsPayloads(values, beneficiary_vetting_id, existingRows = []) {
  const c = values.company || {}
  const races = Array.isArray(c.demographics_race) ? c.demographics_race.filter(Boolean) : []
  const sexes = Array.isArray(c.demographics_gender) ? c.demographics_gender.filter(Boolean) : []
  const ageGroups = Array.isArray(c.demographics_age_groups) ? c.demographics_age_groups.filter(Boolean) : []
  if (races.length === 0 || sexes.length === 0 || ageGroups.length === 0) {
    return []
  }
  const toInt = (v) => {
    const n = v === '' || v == null ? 0 : Number(v)
    return Number.isInteger(n) ? n : Math.floor(Number(n)) || 0
  }
  const getExistingNumberOfPeople = (race, sex, age_group) => {
    const row = (existingRows || []).find(
      (r) => r.race === race && r.sex === sex && r.age_group === age_group
    )
    return row != null ? toInt(row.number_of_people) : 0
  }
  const payloads = []
  for (const race of races) {
    for (const sex of sexes) {
      for (const age_group of ageGroups) {
        payloads.push({
          beneficiary_vetting: beneficiary_vetting_id,
          race,
          sex,
          age_group,
          number_of_people: getExistingNumberOfPeople(race, sex, age_group)
        })
      }
    }
  }
  return payloads
}

export function getDefaultInitialValues() {
  return {
    id: null,
    status: 'vetting_in_progress',
    company: {
      id: null,
      name: '',
      website: '',
      slug: '',
      core_businesses: '', // single-select dropdown - payload converts to array for backend
      target_population: [],
      programmes_and_services: '',
      distribution_channel: [],
      has_sla: false,
      has_consent_form: false,
      has_policy_documents: false,
      additionalComments: '',
      full_time_females: null,
      full_time_males: null,
      number_of_volunteers: null,
      reliance_on_sa_harvest: '',
      transport_capacity: '',
      proposal_writing_capabilities: '',
      digital_capabilities: '',
      meal_distribution: '',
      meals_provided: [],
      days_of_week: [],
      feeding_history_last_date: '',
      feeding_frequency: '',
      number_of_people_served: '',
      is_registered_npo: false,
      npo_number: null,
      npo_certificate_upload: null,
      is_registered_with_dsd: false,
      dsd_registration_number: null,
      pbo_certificate_upload: null,
      food_storage_adequate: [],
      kitchen_facility_images: null,
      has_kitchen_cleanliness: false,
      has_access_to_water: false,
      has_hygiene_facilities: false,
      is_pest_free: false,
      infrastructure_checks_adequate: [],
      has_access_for_delivery: false,
      has_parking_security: false,
      has_proximity_to_police: false,
      demographics_race: [],
      demographics_gender: [],
      demographics_age_groups: [],
      number_females_served: '',
      number_males_served: '',
      number_african_served: '',
      number_coloured_served: '',
      number_indian_asian_served: '',
      number_white_served: '',
      addresses: [],
      contacts: [],
      tags: []
    }
  }
}
